import { useState, useEffect } from "react";

const CORE_QUESTIONS = [
  { id: "project_type", label: "Project type", type: "select", options: ["Music Video", "Short Film", "Feature", "Commercial", "Documentary", "Branded Content", "Other"] },
  { id: "delivery", label: "Primary delivery", type: "select", options: ["Cinema / DCP", "Streaming (Hulu, Amazon, Apple TV+)", "Streaming (Netflix)", "YouTube / Vimeo", "Broadcast TV", "Social (Instagram, TikTok)", "Multiple"] },
  { id: "runtime", label: "Approximate runtime", type: "text", placeholder: "e.g. 4 min, 90 min" },
  { id: "deadline", label: "Final delivery deadline", type: "text", placeholder: "MM/DD/YYYY" },
  { id: "camera", label: "Camera(s) / format", type: "text", placeholder: "e.g. ARRI ALEXA, Sony FX3 S-Log3, iPhone ProRes" },
  { id: "mood_words", label: "3–5 words that describe the intended mood", type: "text", placeholder: "e.g. cold, isolated, dream-like, urgent, raw" },
];

const EDIT_QUESTIONS = [
  { id: "edit_ref_videos", label: "Reference cuts, films, or videos", type: "textarea", placeholder: "Be specific about what you like — the pacing, not just the vibe. Name the sequence if you can." },
  { id: "pacing", label: "Overall pacing", type: "scale", labels: ["Slow / Contemplative", "Driven / Urgent"] },
  { id: "cut_style", label: "Cut style", type: "scale", labels: ["Hard cuts only", "Mixed / Fluid"] },
  { id: "transitions", label: "Transition approach", type: "multicheck", options: ["Hard cuts", "Dissolves", "Whip pans", "Match cuts", "Jump cuts", "Speed ramps", "Smash cuts", "No preference"] },
  { id: "speed_ramps", label: "Speed ramp / time manipulation notes", type: "textarea", placeholder: "Where, how often, how extreme? Or if you'd prefer none." },
  { id: "music_handling", label: "Music / temp track", type: "textarea", placeholder: "Is there a locked track, a temp, or is music TBD? Any cut-to-beat expectations?" },
  { id: "graphics_scope", label: "Graphics / titles / VFX scope", type: "textarea", placeholder: "What's in scope — lower thirds, title cards, VFX pulls, motion graphics?" },
  { id: "edit_avoid", label: "Editing choices you actively dislike", type: "textarea", placeholder: "e.g. over-use of slow mo, too many dissolves, cutting against the music" },
];

const COLOR_QUESTIONS = [
  { id: "ref_films", label: "Reference films or videos (name or link)", type: "textarea", placeholder: "Be specific about what you like — the grade, not just the vibe." },
  { id: "ref_stills", label: "Reference stills or photographers", type: "textarea", placeholder: "Photographers, paintings, album artwork, anything visual." },
  { id: "contrast", label: "Contrast preference", type: "scale", labels: ["Flat / Lifted", "Punchy / Crushed"] },
  { id: "saturation", label: "Saturation preference", type: "scale", labels: ["Desaturated", "Saturated"] },
  { id: "temperature", label: "Overall temperature bias", type: "scale", labels: ["Cool / Blue", "Warm / Orange"] },
  { id: "shadow_bias", label: "Shadow color bias", type: "text", placeholder: "e.g. teal-green, neutral, lifted warm" },
  { id: "highlight_bias", label: "Highlight color bias", type: "text", placeholder: "e.g. warm amber, cool white, neutral" },
  { id: "skin_priority", label: "Skin tone priority", type: "select", options: ["Critical — protect at all costs", "Important but secondary to the overall look", "Stylized skin is fine if it serves the look"] },
  { id: "skin_notes", label: "Skin tone notes", type: "textarea", placeholder: "Mixed cast, very fair skin, specific concerns from talent..." },
];

const AVOID_QUESTIONS = [
  { id: "hated", label: "Work you've seen and actively disliked", type: "textarea", placeholder: "Be honest. This is the most useful question on this form." },
  { id: "past_feedback", label: "Feedback you've received on past projects that you agreed with", type: "textarea", placeholder: "e.g. 'too slow in the second act', 'too green in the shadows'" },
];

const WORKFLOW_QUESTIONS = [
  { id: "revisions", label: "Revision rounds expected", type: "select", options: ["1 round", "2 rounds", "3 rounds", "TBD / flexible"] },
  { id: "approval", label: "Who has final approval", type: "text", placeholder: "e.g. Director only, Director + Label, Client + Agency" },
  { id: "offline", label: "Picture lock status", type: "select", options: ["Yes — locked picture", "No — edit may still change", "Partially locked", "N/A — this is for the edit"] },
  { id: "notes", label: "Anything else we should know", type: "textarea", placeholder: "Budget constraints, technical issues, personal context, etc." },
];

const SECTION_DATA = {
  core: { questions: CORE_QUESTIONS },
  edit: { questions: EDIT_QUESTIONS },
  color: { questions: COLOR_QUESTIONS },
  avoid: { questions: AVOID_QUESTIONS },
  workflow: { questions: WORKFLOW_QUESTIONS },
};

export default function PreRollIntake() {
  const [phase, setPhase] = useState("configure");
  const [activeModules, setActiveModules] = useState({ edit: true, color: true });
  const [activeSection, setActiveSection] = useState(0);
  const [answers, setAnswers] = useState({});
  const [recipientEmail, setRecipientEmail] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [projectName, setProjectName] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const to = params.get("to");
    if (to) setRecipientEmail(decodeURIComponent(to));
    const edit = params.get("edit");
    const color = params.get("color");
    const project = params.get("project");
    if (project) setProjectName(decodeURIComponent(project));
    // If module params exist in URL, skip configure screen
    if (edit !== null || color !== null) {
      setActiveModules({
        edit: edit !== "false",
        color: color !== "false",
      });
      setPhase("form");
    }
  }, []);

  const toggleModule = (key) => setActiveModules((p) => ({ ...p, [key]: !p[key] }));

  function buildSections(mods) {
    let n = 2;
    const secs = [
      { id: "core", label: "01 — Project" },
      { id: "treatment", label: "02 — Treatment" },
    ];
    if (mods.edit) { n++; secs.push({ id: "edit", label: `${pad(n)} — Edit` }); }
    if (mods.color) { n++; secs.push({ id: "color", label: `${pad(n)} — Color` }); }
    n++; secs.push({ id: "avoid", label: `${pad(n)} — What to Avoid` });
    n++; secs.push({ id: "workflow", label: `${pad(n)} — Workflow` });
    return secs;
  }

  const pad = (n) => String(n).padStart(2, "0");
  const sections = buildSections(activeModules);
  const set = (id, val) => setAnswers((p) => ({ ...p, [id]: val }));
  const toggleCheck = (id, val) => {
    const prev = answers[id] || [];
    set(id, prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
  };

  const allQ = sections.flatMap((s) => SECTION_DATA[s.id]?.questions || []);
  const answered = Object.keys(answers).filter((k) => {
    const v = answers[k]; return Array.isArray(v) ? v.length > 0 : v?.toString().trim();
  }).length;
  const progress = Math.round((answered / Math.max(allQ.length, 1)) * 100);

  const generateLink = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("edit", activeModules.edit ? "true" : "false");
    params.set("color", activeModules.color ? "true" : "false");
    if (projectName.trim()) params.set("project", projectName.trim());
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    setShareUrl(url);
  };

  const startForm = () => {
    setActiveSection(0);
    setPhase("form");
  };
  const backToConfigure = () => setPhase("configure");

  const handleSubmit = async () => {
    setPhase("submitting");
    try {
      const answers_payload = {};
      sections.forEach((sec) => {
        const qs = SECTION_DATA[sec.id]?.questions || [];
        qs.forEach((q) => {
          const val = answers[q.id];
          if (val !== undefined && val !== "") {
            answers_payload[q.label] = Array.isArray(val) ? val.join(", ") : String(val);
          }
        });
      });
      if (answers.treatment_file) answers_payload["Treatment file"] = answers.treatment_file;
      if (answers.treatment_link) answers_payload["Treatment link"] = answers.treatment_link;
      if (answers.t_departed) answers_payload["Changes from treatment"] = answers.t_departed;
      answers_payload["Modules"] = [
        activeModules.edit ? "Edit" : null,
        activeModules.color ? "Color" : null,
      ].filter(Boolean).join(" + ");
      const _urlParams = new URLSearchParams(window.location.search);
      const _urlProject = _urlParams.get("project");
      if (_urlProject) answers_payload["Project name (from sender)"] = decodeURIComponent(_urlProject);
      else if (projectName) answers_payload["Project name (from sender)"] = projectName;

      await fetch("/.netlify/functions/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: recipientEmail, answers: answers_payload }),
      });
    } catch (e) {}
    setPhase("submitted");
  };

  // ── CONFIGURE ──
  if (phase === "configure") {
    const noneSelected = !activeModules.edit && !activeModules.color;
    return (
      <div style={s.root}>
        <style>{css}</style>
        <div style={s.configWrap}>
          <div style={s.configInner}>
            <div style={s.configBrand}>
              <span style={s.brandMark}>PR</span>
              <span style={s.brandName}>PreRoll</span>
            </div>
            <h1 style={s.configTitle}>Set up your brief</h1>
            <p style={s.configSub}>Select which disciplines are on this project. Only the relevant sections will appear in the brief.</p>

            <div style={s.questionBlock}>
              <label style={s.label}>Project name</label>
              <input
                style={s.input}
                placeholder="e.g. Client Name — Project"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </div>
            <div style={s.configCards}>
              {[
                { key: "edit", icon: "✂", title: "Editor", desc: "Pacing, cut style, transitions, speed ramps, music handling, graphics scope." },
                { key: "color", icon: "◑", title: "Colorist", desc: "Visual references, grade parameters, skin tone notes, shadow and highlight bias." },
              ].map(({ key, icon, title, desc }) => {
                const on = activeModules[key];
                return (
                  <div key={key} className="config-card" onClick={() => toggleModule(key)} style={{
                    ...s.configCard,
                    borderColor: on ? "#c8f064" : "#1e1e1e",
                    background: on ? "rgba(200,240,100,0.04)" : "transparent",
                  }}>
                    <div style={s.configCardTop}>
                      <span style={{ ...s.configIcon, color: on ? "#c8f064" : "#2a2a2a" }}>{icon}</span>
                      <div style={{ ...s.configCheck, background: on ? "#c8f064" : "transparent", borderColor: on ? "#c8f064" : "#2a2a2a" }}>
                        {on && <span style={{ color: "#0a0a0a", fontSize: 10 }}>✓</span>}
                      </div>
                    </div>
                    <h3 style={{ ...s.configCardTitle, color: on ? "#e8e4dc" : "#444" }}>{title}</h3>
                    <p style={s.configCardDesc}>{desc}</p>
                  </div>
                );
              })}
            </div>

            {noneSelected && <p style={s.configWarning}>Select at least one to continue.</p>}

            {!shareUrl ? (
              <button className="go-btn" onClick={generateLink} disabled={noneSelected} style={{
                ...s.goBtn, opacity: noneSelected ? 0.3 : 1, cursor: noneSelected ? "not-allowed" : "pointer",
              }}>
                Build Brief →
              </button>
            ) : (
              <button className="go-btn" onClick={startForm} style={{...s.goBtn, background: "#c8f064", color: "#0a0a0a", cursor: "pointer"}}>
                Preview Brief →
              </button>
            )}
            {shareUrl ? (
              <div style={s.shareBox}>
                <p style={s.shareLabel}>Client link — copied to clipboard</p>
                <div style={s.shareRow}>
                  <span style={s.shareUrl}>{shareUrl}</span>
                  <button
                    className="copy-btn"
                    onClick={() => navigator.clipboard && navigator.clipboard.writeText(shareUrl)}
                    style={s.copyBtn}
                  >Copy</button>
                </div>
                <p style={s.shareNote}>Send this link to your client. They'll go straight to the brief — no configure screen.</p>
              </div>
            ) : (
              <p style={s.configNote}>Your client will only see the sections you've enabled.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── SUBMITTED ──
  if (phase === "submitting") {
    return (
      <div style={s.root}>
        <style>{css}</style>
        <div style={s.submittedWrap}>
          <div style={s.submittedInner}>
            <p style={{ ...s.submittedSub, color: "#444" }}>Sending brief…</p>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "submitted") {
    return (
      <div style={s.root}>
        <style>{css}</style>
        <div style={s.submittedWrap}>
          <div style={s.submittedInner}>
            <div style={s.checkmark}>✓</div>
            <h2 style={s.submittedTitle}>Brief received.</h2>
            <p style={s.submittedSub}>You'll hear back within 24 hours with a confirmed direction before work begins.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── FORM ──
  const cur = sections[activeSection];
  const isTreatment = cur.id === "treatment";
  const questions = SECTION_DATA[cur.id]?.questions || [];

  return (
    <div style={s.root}>
      <style>{css}</style>

      <div style={s.header}>
        <div style={s.headerInner}>
          <div style={s.headerLeft}>
            <div style={s.brandSmall}>
              <span style={s.brandMarkSm}>PR</span>
              <span style={s.brandNameSm}>PreRoll</span>
            </div>
            <span style={s.headerSep}>—</span>
            <span style={s.headerDoc}>Client Brief</span>
          </div>
          <div style={s.headerRight}>
            <div style={s.modulePills}>
              {activeModules.edit && <span style={s.pill}>✂ Edit</span>}
              {activeModules.color && <span style={s.pill}>◑ Color</span>}
              <button className="reconfigure-btn" onClick={backToConfigure} style={s.reconfigureBtn} title="Change modules">
                ↩ Reconfigure
              </button>
            </div>
            <div style={s.progressLabel}>{progress}% complete</div>
            <div style={s.progressBar}><div style={{ ...s.progressFill, width: `${progress}%` }} /></div>
          </div>
        </div>
      </div>

      <div style={s.body}>
        <nav style={s.nav}>
          {sections.map((sec, i) => (
            <div key={sec.id} className="section-tab" onClick={() => setActiveSection(i)} style={{
              ...s.navItem,
              color: activeSection === i ? "#c8f064" : "#333",
              borderLeftColor: activeSection === i ? "#c8f064" : "transparent",
            }}>
              {sec.label}
            </div>
          ))}
        </nav>

        <div style={s.form}>
          <div style={s.sectionHeader}>
            <h2 style={s.sectionTitle}>{cur.label}</h2>
            {cur.id === "edit" && <span style={s.sectionBadge}>✂ Editor</span>}
            {cur.id === "color" && <span style={s.sectionBadge}>◑ Colorist</span>}
          </div>

          {isTreatment ? (
            <div style={s.questions}>
              <div style={s.questionBlock}>
                <label style={s.label}>Upload your treatment</label>
                <p style={s.subtext}>PDF, Word doc, or any file you already have. We'll pull everything relevant so you don't have to repeat yourself.</p>
                <div className="upload-box" style={{ ...s.uploadBox, borderColor: answers.treatment_file ? "#c8f064" : "#1e1e1e" }}
                  onClick={() => document.getElementById("treatment-upload").click()}>
                  {answers.treatment_file ? (
                    <div style={s.uploadedFile}>
                      <span style={{ color: "#c8f064", marginRight: 10 }}>✓</span>
                      <span style={s.uploadedName}>{answers.treatment_file}</span>
                      <button style={s.removeFile} onClick={(e) => { e.stopPropagation(); set("treatment_file", ""); }}>✕</button>
                    </div>
                  ) : (
                    <div style={s.uploadPrompt}>
                      <span style={s.uploadIcon}>↑</span>
                      <span style={s.uploadText}>Click to upload</span>
                      <span style={s.uploadSub}>PDF, DOC, DOCX, or TXT</span>
                    </div>
                  )}
                </div>
                <input id="treatment-upload" type="file" accept=".pdf,.doc,.docx,.txt" style={{ display: "none" }}
                  onChange={(e) => { if (e.target.files[0]) set("treatment_file", e.target.files[0].name); }} />
              </div>
              <div style={s.questionBlock}>
                <label style={s.label}>Or paste a link</label>
                <input style={s.input} placeholder="Google Doc, Dropbox, Frame.io, etc." value={answers.treatment_link || ""} onChange={(e) => set("treatment_link", e.target.value)} />
              </div>
              <div style={s.questionBlock}>
                <label style={s.label}>Anything that changed from the treatment</label>
                <textarea style={s.textarea} placeholder="Anything that shifted in production that affects the work." value={answers.t_departed || ""} onChange={(e) => set("t_departed", e.target.value)} rows={3} />
              </div>
            </div>
          ) : (
            <div style={s.questions}>
              {questions.map((q) => (
                <div key={q.id} style={s.questionBlock}>
                  <label style={s.label}>{q.label}</label>
                  {q.type === "text" && <input style={s.input} placeholder={q.placeholder} value={answers[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} />}
                  {q.type === "textarea" && <textarea style={s.textarea} placeholder={q.placeholder} value={answers[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} rows={4} />}
                  {q.type === "select" && (
                    <select style={s.select} value={answers[q.id] || ""} onChange={(e) => set(q.id, e.target.value)}>
                      <option value="">Select…</option>
                      {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                  {q.type === "scale" && (
                    <div style={s.scaleWrap}>
                      <span style={s.scaleLabel}>{q.labels[0]}</span>
                      <div style={s.scaleBtns}>
                        {[1, 2, 3, 4, 5].map((v) => (
                          <button key={v} className="scale-btn" onClick={() => set(q.id, v)} style={{
                            ...s.scaleBtn,
                            background: answers[q.id] === v ? "#c8f064" : "transparent",
                            color: answers[q.id] === v ? "#0a0a0a" : "#333",
                            borderColor: answers[q.id] === v ? "#c8f064" : "#1e1e1e",
                          }}>{v}</button>
                        ))}
                      </div>
                      <span style={s.scaleLabel}>{q.labels[1]}</span>
                    </div>
                  )}
                  {q.type === "multicheck" && (
                    <div style={s.checkGrid}>
                      {q.options.map((opt) => {
                        const checked = (answers[q.id] || []).includes(opt);
                        return (
                          <div key={opt} className="check-item" onClick={() => toggleCheck(q.id, opt)} style={{
                            ...s.checkItem,
                            borderColor: checked ? "#c8f064" : "#1e1e1e",
                            color: checked ? "#c8f064" : "#333",
                            background: checked ? "rgba(200,240,100,0.05)" : "transparent",
                          }}>{opt}</div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <div style={s.navButtons}>
            {activeSection > 0 ? (
              <button onClick={() => setActiveSection((i) => i - 1)} style={s.backBtn}>← Back</button>
            ) : (
              <button onClick={backToConfigure} style={s.backBtn}>← Reconfigure</button>
            )}
            <div style={{ flex: 1 }} />
            {activeSection < sections.length - 1 ? (
              <button className="next-btn" onClick={() => setActiveSection((i) => i + 1)} style={s.nextBtn}>Next →</button>
            ) : (
              <button className="next-btn" onClick={handleSubmit} style={s.nextBtn}>Submit Brief</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;1,400&family=DM+Mono:wght@300;400;500&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  ::selection { background: #c8f064; color: #0a0a0a; }
  input:focus, textarea:focus, select:focus { outline: none; border-color: #c8f064 !important; }
  .section-tab { cursor: pointer; transition: all 0.15s; }
  .section-tab:hover { color: #888 !important; }
  .next-btn:hover { background: #c8f064 !important; color: #0a0a0a !important; }
  .scale-btn:hover { border-color: #c8f064 !important; color: #c8f064 !important; }
  .check-item { cursor: pointer; transition: all 0.15s; }
  .check-item:hover { border-color: #444 !important; color: #888 !important; }
  .config-card { cursor: pointer; transition: all 0.2s; user-select: none; }
  .go-btn:hover:not(:disabled) { background: #c8f064 !important; color: #0a0a0a !important; }
  .upload-box { transition: border-color 0.15s; cursor: pointer; }
  .copy-btn:hover { background: #c8a96e !important; }
  .upload-box:hover { border-color: #444 !important; }
  .reconfigure-btn:hover { color: #c8f064 !important; border-color: #c8f064 !important; }
  select option { background: #0d0d0d; color: #e8e4dc; }
`;

const s = {
  root: { minHeight: "100vh", background: "#0a0a0a", color: "#e8e4dc", fontFamily: "'DM Mono', monospace" },
  // Brand
  brandMark: { fontSize: 11, letterSpacing: "0.15em", color: "#0a0a0a", background: "#c8f064", padding: "3px 7px", fontWeight: 500 },
  brandName: { fontSize: 14, letterSpacing: "0.1em", color: "#e8e4dc", fontFamily: "'Playfair Display', serif", fontStyle: "italic" },
  brandMarkSm: { fontSize: 9, letterSpacing: "0.15em", color: "#0a0a0a", background: "#c8f064", padding: "2px 5px", fontWeight: 500 },
  brandNameSm: { fontSize: 12, letterSpacing: "0.08em", color: "#555", fontFamily: "'Playfair Display', serif", fontStyle: "italic" },
  // Configure
  configWrap: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 24px" },
  configInner: { maxWidth: 560, width: "100%", display: "flex", flexDirection: "column", gap: 28 },
  configBrand: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  configTitle: { fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 400, color: "#e8e4dc" },
  configSub: { fontSize: 12, color: "#444", lineHeight: 1.8, marginTop: -4 },
  configCards: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
  configCard: { border: "1px solid", padding: "24px 20px" },
  configCardTop: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
  configIcon: { fontSize: 18, transition: "color 0.2s" },
  configCheck: { width: 18, height: 18, border: "1px solid", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", flexShrink: 0 },
  configCardTitle: { fontSize: 14, letterSpacing: "0.04em", marginBottom: 8, transition: "color 0.2s" },
  configCardDesc: { fontSize: 11, color: "#2a2a2a", lineHeight: 1.7 },
  configWarning: { fontSize: 11, color: "#444", textAlign: "center" },
  goBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#e8e4dc", fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", padding: "15px", transition: "all 0.2s", width: "100%" },
  configNote: { fontSize: 10, color: "#222", textAlign: "center", letterSpacing: "0.05em" },
  shareBox: { background: "#0d0d0b", border: "1px solid #c8f064", padding: "20px", display: "flex", flexDirection: "column", gap: 10 },
  shareLabel: { fontSize: 9, letterSpacing: "0.15em", textTransform: "uppercase", color: "#c8f064" },
  shareRow: { display: "flex", alignItems: "center", gap: 10 },
  shareUrl: { fontSize: 11, color: "#888", flex: 1, wordBreak: "break-all", fontFamily: "'DM Mono', monospace" },
  copyBtn: { background: "#c8f064", border: "none", color: "#0a0a0a", fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.1em", padding: "8px 16px", cursor: "pointer", flexShrink: 0 },
  shareNote: { fontSize: 10, color: "#333", lineHeight: 1.6 },
  // Header
  header: { borderBottom: "1px solid #141414", padding: "20px 48px" },
  headerInner: { maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24 },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  brandSmall: { display: "flex", alignItems: "center", gap: 8 },
  headerSep: { color: "#1e1e1e", fontSize: 12 },
  headerDoc: { fontSize: 11, color: "#333", letterSpacing: "0.08em" },
  headerRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, minWidth: 200 },
  modulePills: { display: "flex", gap: 8, alignItems: "center" },
  pill: { fontSize: 9, letterSpacing: "0.1em", color: "#333", border: "1px solid #1a1a1a", padding: "3px 8px" },
  reconfigureBtn: { background: "transparent", border: "1px solid #1a1a1a", color: "#2a2a2a", fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "0.1em", padding: "3px 10px", cursor: "pointer", transition: "all 0.15s" },
  progressLabel: { fontSize: 10, color: "#2a2a2a", letterSpacing: "0.08em" },
  progressBar: { width: "100%", height: 1, background: "#141414", overflow: "hidden" },
  progressFill: { height: "100%", background: "#c8f064", transition: "width 0.4s ease" },
  // Body
  body: { maxWidth: 960, margin: "0 auto", display: "flex", padding: "0 48px 80px" },
  nav: { width: 190, paddingTop: 48, flexShrink: 0 },
  navItem: { fontSize: 10, letterSpacing: "0.07em", padding: "9px 0 9px 16px", borderLeft: "1px solid", marginBottom: 2, transition: "all 0.15s" },
  form: { flex: 1, paddingTop: 48, paddingLeft: 60 },
  sectionHeader: { marginBottom: 36, paddingBottom: 20, borderBottom: "1px solid #141414", display: "flex", alignItems: "baseline", gap: 14 },
  sectionTitle: { fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 400, color: "#e8e4dc" },
  sectionBadge: { fontSize: 9, color: "#333", letterSpacing: "0.12em", border: "1px solid #1a1a1a", padding: "3px 10px" },
  questions: { display: "flex", flexDirection: "column", gap: 30 },
  questionBlock: { display: "flex", flexDirection: "column", gap: 9 },
  label: { fontSize: 10, letterSpacing: "0.1em", color: "#666", textTransform: "uppercase" },
  subtext: { fontSize: 11, color: "#2a2a2a", lineHeight: 1.7 },
  input: { background: "transparent", border: "1px solid #1a1a1a", padding: "11px 14px", color: "#e8e4dc", fontFamily: "'DM Mono', monospace", fontSize: 13, width: "100%", transition: "border-color 0.15s" },
  textarea: { background: "transparent", border: "1px solid #1a1a1a", padding: "11px 14px", color: "#e8e4dc", fontFamily: "'DM Mono', monospace", fontSize: 13, resize: "vertical", lineHeight: 1.65, width: "100%", transition: "border-color 0.15s" },
  select: { background: "#0d0d0d", border: "1px solid #1a1a1a", padding: "11px 14px", color: "#e8e4dc", fontFamily: "'DM Mono', monospace", fontSize: 13, width: "100%", cursor: "pointer", appearance: "none", transition: "border-color 0.15s" },
  scaleWrap: { display: "flex", alignItems: "center", gap: 14 },
  scaleLabel: { fontSize: 10, color: "#2a2a2a", letterSpacing: "0.04em", minWidth: 90 },
  scaleBtns: { display: "flex", gap: 7 },
  scaleBtn: { width: 38, height: 38, border: "1px solid", fontFamily: "'DM Mono', monospace", fontSize: 12, cursor: "pointer", transition: "all 0.15s" },
  checkGrid: { display: "flex", flexWrap: "wrap", gap: 8 },
  checkItem: { fontSize: 11, letterSpacing: "0.05em", border: "1px solid", padding: "7px 14px" },
  uploadBox: { border: "1px dashed", padding: "36px 24px", textAlign: "center", marginTop: 8 },
  uploadPrompt: { display: "flex", flexDirection: "column", alignItems: "center", gap: 7 },
  uploadIcon: { fontSize: 20, color: "#222" },
  uploadText: { fontSize: 12, color: "#444" },
  uploadSub: { fontSize: 10, color: "#222" },
  uploadedFile: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
  uploadedName: { fontSize: 12, color: "#e8e4dc" },
  removeFile: { background: "none", border: "none", color: "#333", cursor: "pointer", fontSize: 11 },
  navButtons: { display: "flex", alignItems: "center", marginTop: 52, paddingTop: 28, borderTop: "1px solid #141414" },
  backBtn: { background: "transparent", border: "none", color: "#2a2a2a", fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.08em", cursor: "pointer", padding: "10px 0", transition: "color 0.15s" },
  nextBtn: { background: "transparent", border: "1px solid #2a2a2a", color: "#e8e4dc", fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", padding: "12px 26px", cursor: "pointer", transition: "all 0.2s" },
  // Submitted
  submittedWrap: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" },
  submittedInner: { textAlign: "center", maxWidth: 360 },
  checkmark: { fontSize: 26, color: "#c8f064", marginBottom: 22 },
  submittedTitle: { fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 400, color: "#e8e4dc", marginBottom: 12 },
  submittedSub: { fontSize: 12, color: "#333", lineHeight: 1.8 },
};
